import type { LucideIcon } from 'lucide-react';
import {
  Bot,
  HelpCircle,
  Lightbulb,
  ListChecks,
  MessageCircle,
  PenLine,
  Repeat,
  Rocket,
  ShieldCheck,
  Wallet,
} from 'lucide-react';

/**
 * Structured guide content for the customer-facing chatbot setup guide.
 *
 * Each section is a `GuideSection` whose `body` is an array of `GuideBlock`
 * descriptors. `ChatbotGuidePage` renders these blocks — keeping the content
 * data-shaped means future edits don't require touching JSX.
 */

export type CalloutTone = 'tip' | 'important' | 'note';

export type GuideBlock =
  | { kind: 'paragraph'; text: string }
  /** A bullet list. Each bullet may carry an optional bold label rendered before the text. */
  | { kind: 'bullets'; items: Array<{ label?: string; text: string }> }
  /** An ordered (numbered) list. */
  | { kind: 'numbered'; items: Array<{ label?: string; text: string }> }
  /** Side-bar callout (tip / important / note). */
  | { kind: 'callout'; tone: CalloutTone; title?: string; text: string }
  /** A simple 2-column "Channel | Status" grid used in the channels section. */
  | {
      kind: 'grid';
      headers: [string, string];
      rows: Array<{ name: string; status: string; description?: string }>;
    }
  /** A Q/A pair used in the FAQ section. */
  | { kind: 'qa'; items: Array<{ question: string; answer: string }> };

export interface GuideCta {
  label: string;
  href: string;
}

export interface GuideSection {
  /** Anchor id — must be globally unique. Used by the TOC scroll links. */
  id: string;
  /** Section title shown as the section heading and in the TOC. */
  title: string;
  /** One-line intro rendered between the title and the body. */
  intro: string;
  /** Lucide icon shown alongside the section title and in the TOC. */
  icon: LucideIcon;
  /** Body blocks rendered in order. */
  body: GuideBlock[];
  /** Optional primary CTA rendered at the foot of the section. */
  cta?: GuideCta;
}

export const GUIDE_SECTIONS: GuideSection[] = [
  // ── Section 1 ────────────────────────────────────────────────────────────
  {
    id: 'what-it-does',
    title: 'What your chatbot does',
    icon: Bot,
    intro:
      "Your chatbot answers customer questions for you, automatically, 24×7. It only uses knowledge you've approved — it never makes things up. When it can't answer, it raises a request to your team so nothing falls through the cracks.",
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'It answers',
            text:
              'common questions like "What\'s your return policy?", "How long does delivery take?", "What payment methods do you accept?" — using the answers you\'ve written and approved.',
          },
          {
            label: 'It knows your customer',
            text:
              '— when a logged-in customer asks "where is my order?", the bot already sees their orders and can reply with the specific one.',
          },
          {
            label: 'It raises support requests',
            text:
              'for things it can\'t answer — those land in your Support queue with a reference number (SR-XXXX).',
          },
          {
            label: "It can't lie or invent",
            text:
              "— it's built to refuse rather than guess. If your knowledge doesn't cover something, it escalates honestly.",
          },
          {
            label: 'It learns from you',
            text:
              "— every question it couldn't answer, and every case your team resolves, becomes new draft knowledge for you to approve.",
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'important',
        title: 'Important',
        text:
          "The bot will only ever answer from knowledge you've approved. Drafts — whether you wrote them or the AI suggested them — are invisible to your customers until you approve them.",
      },
    ],
  },

  // ── Section 2 ────────────────────────────────────────────────────────────
  {
    id: 'quick-start',
    title: 'Quick start: get your bot live in 30 minutes',
    icon: ListChecks,
    intro:
      'The fastest path from sign-up to a working chatbot is four steps. You can do them in one sitting.',
    body: [
      {
        kind: 'numbered',
        items: [
          {
            label: 'Answer the 10 setup questions.',
            text:
              'Go to "Set up your knowledge base" on the Chatbot page. Each question — "What are your business hours?", "What is your return policy?", etc. — is a knowledge entry the bot will use. Type your real answers in plain English.',
          },
          {
            label: 'Mark each as ready.',
            text:
              'When you\'ve written an answer, hit "Approve". Until you do, the bot won\'t use it.',
          },
          {
            label: 'Preview it.',
            text:
              "Open the Preview, switch to Customer view, and ask the bot the same questions a customer would. Make sure it answers the way you'd want.",
          },
          {
            label: 'Share the chat widget',
            text:
              'with your customers (your developer / agency will embed it on your site or portal).',
          },
        ],
      },
    ],
    cta: { label: 'Open Bot Setup', href: '/chatbot' },
  },

  // ── Section 2b ───────────────────────────────────────────────────────────
  {
    id: 'full-setup',
    title: 'Set up your chatbot, step by step',
    icon: Rocket,
    intro:
      "Quick start gets you live fast — this is the complete walk-through of every screen, so your bot looks and sounds like your brand before it ever talks to a customer.",
    body: [
      {
        kind: 'paragraph',
        text:
          'Everything lives on the Chatbot page, organised into five tabs — Setup, Knowledge, Insights, Test and Settings. Work through them left to right the first time; after go-live you\'ll mostly live in Knowledge and Insights.',
      },
      {
        kind: 'numbered',
        items: [
          {
            label: 'Setup tab — answer the starter questions.',
            text:
              'Open Chatbot → Setup. You\'ll see a short list of the questions customers ask most — business hours, return policy, delivery times, payment methods, and so on. Each answer you write becomes a knowledge entry the bot can use. Write real answers in plain English; you can refine them any time.',
          },
          {
            label: 'Knowledge tab — review and Approve.',
            text:
              'Every answer starts as a draft. Open Knowledge, check each entry reads the way you\'d want, and hit Approve — the bot serves ONLY approved entries, so drafts stay invisible to customers. Add your own entries here too: Title = the question in your customer\'s words, Content = the full answer, Category = a short label like Shipping, Returns or Billing.',
          },
          {
            label: 'Settings tab — make it look like you.',
            text:
              'Open Settings → Appearance and set the bot Name and Opening greeting (the first line a customer sees), an optional Header title (defaults to the bot name), your Primary / Header / Header-text colours, Mode (light or dark) and on-screen Position, what the Header shows (name only, your logo, or both), the Logo shape, and upload your avatar/logo. The live preview beside the form updates as you type.',
          },
          {
            label: 'Test tab — try it as a customer.',
            text:
              'Open Test, switch to Customer view, and ask the questions a real customer would. Confirm the answers are correct and the tone is yours. If something reads wrong, fix the entry back in Knowledge and test again — it\'s instant.',
          },
          {
            label: 'Embed it on your website.',
            text:
              'Open Chatbot → Embed. Three steps: (1) make sure your organisation\'s website matches the domain you\'ll embed on — the widget authenticates by your site\'s Origin, so that\'s the only allow-listing needed; (2) tweak placement and copy the snippet (plain HTML or React); (3) paste it just before your site\'s closing </body> tag. Your origin is auto-allowed within about a minute — no ticket, and no secret ever sits in your page.',
          },
          {
            label: 'Insights tab — keep it improving.',
            text:
              'Once live, Insights shows what customers asked that the bot couldn\'t answer (knowledge gaps) and turns resolved support cases into draft knowledge for you to approve. A quick weekly review keeps coverage climbing.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Tip',
        text:
          'Set your brand colours and avatar before you embed — the widget reads them live, so customers see your branding from the very first chat.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Adding a domain',
        text:
          "The widget is authorised by your site's Origin, matched against your organisation's website (plus any additional allowed origins). Set that to the exact site you'll embed on; changes go live in about a minute — no allowlist ticket needed.",
      },
      {
        kind: 'callout',
        tone: 'important',
        title: 'If the bot won\'t answer something',
        text:
          "It only serves APPROVED knowledge. If the bot says it can't help with something you expected it to know, open the Knowledge tab and confirm that entry is Approved rather than still a draft.",
      },
    ],
    cta: { label: 'Open the Chatbot page', href: '/chatbot' },
  },

  // ── Section 3 ────────────────────────────────────────────────────────────
  {
    id: 'writing-knowledge',
    title: 'Writing good knowledge entries',
    icon: PenLine,
    intro:
      "The bot is only as good as what you've taught it. A clear, complete knowledge entry takes 2 minutes to write and answers thousands of customers.",
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Title',
            text:
              '= the question your customer would ask, in their words. "What is your return policy?" not "Return policy v2".',
          },
          {
            label: 'Content',
            text:
              '= the full answer, in plain English. Imagine writing it to one polite customer.',
          },
          {
            label: 'Category',
            text:
              '= group related topics. Use short labels: Shipping, Returns, Billing, Orders, Account, General. Keep them consistent.',
          },
          {
            label: "Don't",
            text:
              'include: internal codes, jargon, contradictions across entries, outdated info, or "contact support" deflections — the bot already escalates honestly when it can\'t answer.',
          },
          {
            label: 'Update when business changes.',
            text:
              'If your return window changes from 14 to 30 days, edit that one entry. The bot starts using the new answer immediately after you approve.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Tip',
        text:
          "One answer per question. Don't bundle multiple questions into one entry — the bot retrieves better when each entry covers exactly one topic.",
      },
    ],
  },

  // ── Section 4 ────────────────────────────────────────────────────────────
  {
    id: 'gaps',
    title: "What customers ask that we can't answer yet",
    icon: Lightbulb,
    intro:
      "Every time the bot couldn't answer a question, it logs it. The Knowledge Gaps panel groups those questions into topics so you can see exactly what's missing — and fix it in minutes.",
    body: [
      {
        kind: 'bullets',
        items: [
          { text: 'Open the Knowledge Gaps section on the Chatbot page.' },
          {
            text:
              'Click Analyze gaps — the AI clusters recent unanswered questions into topics with example questions. (This takes ~15 seconds.)',
          },
          {
            text:
              'For each gap that matters, click Draft an article — that pre-fills the Add-knowledge form with the topic. Write the answer, hit Approve, done.',
          },
          {
            text:
              'Review gaps weekly. A small habit that compounds — the more you close, the less your team has to do.',
          },
        ],
      },
    ],
    cta: { label: 'View Knowledge Gaps', href: '/chatbot#gaps' },
  },

  // ── Section 5 ────────────────────────────────────────────────────────────
  {
    id: 'loop-b',
    title: 'How resolving cases makes the bot smarter',
    icon: Repeat,
    intro:
      'When your team resolves a customer support request, the AI drafts a new knowledge-base article from how it was resolved. Real cases your team handles become reusable answers for the next customer.',
    body: [
      {
        kind: 'bullets',
        items: [
          { text: 'Open any support request from the Support queue.' },
          {
            text:
              'Click Resolve request and type how it was resolved in the notes (this is the key bit — the AI uses your resolution wording to draft the article).',
          },
          {
            text:
              'The bot AI-drafts a generalised version (no customer names, no order numbers, just the reusable answer) and saves it as a draft in your knowledge base, tagged "AI-drafted from a resolved request".',
          },
          {
            text:
              "Review it, edit the wording if you want, and hit Approve. From that moment on, the bot can answer that question itself.",
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Tip',
        text:
          'The richer your resolution notes, the better the AI draft. "Confirmed item is in stock; will dispatch tomorrow" is good. "Sorted" is not.',
      },
    ],
    cta: { label: 'Open Support Queue', href: '/support' },
  },

  // ── Section 6 ────────────────────────────────────────────────────────────
  {
    id: 'channels',
    title: 'Where customers will chat',
    icon: MessageCircle,
    intro:
      "Today the bot lives in your portal's chat widget. More channels are on the roadmap.",
    body: [
      {
        kind: 'grid',
        headers: ['Channel', 'Status'],
        rows: [
          {
            name: 'Portal / website widget',
            status: 'Live today',
            description:
              'Your developer embeds it on your site or customer portal.',
          },
          {
            name: 'WhatsApp Business',
            status: 'Coming next',
            description:
              "Will connect via the Meta Business API. Meta's per-conversation fees pass through to you at cost.",
          },
          {
            name: 'Email',
            status: 'Roadmap',
            description: 'For customers who prefer email.',
          },
          {
            name: 'Phone',
            status: 'Roadmap',
            description: 'Voice integration over time.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Note',
        text:
          'A "Previewing as <customer>" selector on the Chatbot page lets you test the bot as if you were a specific customer — useful before turning it on for everyone.',
      },
    ],
  },

  // ── Section 7 ────────────────────────────────────────────────────────────
  {
    id: 'pricing',
    title: 'Pricing & limits',
    icon: Wallet,
    intro:
      'Your plan includes a monthly pool of bot conversations and agent seats. You can grow into a larger plan as your business grows.',
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'A "bot turn"',
            text:
              'is one round-trip — the customer says something, the bot replies. A short customer chat is usually 3–6 turns.',
          },
          {
            label: 'Your plan',
            text:
              "includes a monthly pool of turns. We'll show you a clear usage meter on this page (coming soon).",
          },
          {
            label: 'Approaching your limit?',
            text:
              "You'll see a banner with the option to upgrade. The bot keeps working — we won't break your customer's chat because of a budget alert.",
          },
          {
            label: 'Need more capacity?',
            text:
              'Upgrading is one click — the new pool kicks in immediately.',
          },
        ],
      },
      {
        kind: 'callout',
        tone: 'important',
        title: 'Important',
        text:
          'The bot never charges your end-customer for anything. They chat for free; the plan is what your business pays.',
      },
    ],
  },

  // ── Section 8 ────────────────────────────────────────────────────────────
  {
    id: 'tips',
    title: 'Tips & best practices',
    icon: ShieldCheck,
    intro: 'Small habits that make a big difference over weeks and months.',
    body: [
      {
        kind: 'bullets',
        items: [
          {
            label: 'Approve carefully.',
            text:
              'Don\'t approve drafts blindly. A wrong answer in your knowledge base is worse than a missing one — the bot will confidently give the wrong answer.',
          },
          {
            label: 'Keep entries focused.',
            text:
              'One question per entry. Easier to maintain, easier for the bot to retrieve.',
          },
          {
            label: 'Use Categories consistently.',
            text:
              "Pick 5–8 categories and stick to them. Categories help the bot's opening menu and improve retrieval.",
          },
          {
            label: 'Review weekly.',
            text:
              '15 minutes a week reviewing gaps and draft suggestions keeps the bot fresh.',
          },
          {
            label: 'Update when things change.',
            text:
              'New policy? Edit the entry. The bot picks up the change the moment you re-approve.',
          },
          {
            label: 'Don\'t write "contact us" answers.',
            text:
              "The bot already escalates honestly when it can't help. Telling customers to email you is a deflection, not an answer.",
          },
          {
            label: 'Test the Customer view periodically.',
            text:
              "Open Preview, switch to Customer view, and have a real conversation. You'll catch issues your customers would.",
          },
        ],
      },
    ],
  },

  // ── Section 9 ────────────────────────────────────────────────────────────
  {
    id: 'faq',
    title: 'Frequently asked questions',
    icon: HelpCircle,
    intro: 'The questions tenant owners ask us most.',
    body: [
      {
        kind: 'qa',
        items: [
          {
            question: 'Do I need an API key to add the chatbot to my site?',
            answer:
              "No. The chat widget authenticates by your website's Origin — matched against your Organization website — so it's just a single <script> tag with no key or secret in your page. (An API key is only needed for the separate Bot API — server-to-server programmatic access — not for the embedded widget.)",
          },
          {
            question: 'Can the bot lie or make things up?',
            answer:
              "No — by design, not by hope. It only ever uses knowledge you've approved. If your knowledge doesn't cover a question, the bot escalates honestly to your team instead of guessing.",
          },
          {
            question: 'Can I edit the AI-drafted articles?',
            answer:
              'Yes — drafts are just normal entries you can edit before approving. The "AI-drafted" badge is provenance; it doesn\'t lock anything.',
          },
          {
            question: 'What if my business changes?',
            answer:
              'Edit the relevant knowledge entry and approve it again. The bot starts using the updated answer immediately.',
          },
          {
            question: 'Can the bot raise tickets?',
            answer:
              'Yes — when the bot escalates or the customer reports an issue, it creates a support request in your Support queue with a reference number (SR-XXXX). Your team picks it up from there.',
          },
          {
            question: "What about my customer's privacy?",
            answer:
              "Each customer's data is strictly isolated — the bot only ever sees the one customer it's talking to. Cross-customer information cannot leak. You can also clear conversation history per customer if asked.",
          },
          {
            question: 'Can I see what the bot said to a customer?',
            answer:
              'Yes — every conversation is logged in the Conversations module. Bot turns are recorded just like agent turns; your team gets the full context when picking up an escalation.',
          },
          {
            question: 'What languages does the bot support?',
            answer:
              'English today. Multilingual support is on the roadmap — speak to us if you have a specific language need.',
          },
          {
            question: 'How long do you keep conversation history?',
            answer:
              'Currently retained indefinitely; the customer-facing widget restores the last 20 messages on open. A formal retention policy (e.g. 12 months) is on our roadmap.',
          },
        ],
      },
    ],
  },
];

